// Editores inline reutilizables para la vista previa de propiedades.
// En modo lectura muestran el valor formateado; en modo edición muestran el
// control correspondiente. Todos son controlados: notifican cambios vía onChange.

import React from 'react';
import styles from './PropertyPreviewModal.module.css';

const EmptyMark = ({ required }) => (
  <span className={required ? styles.missingTag : styles.emptyTag}>
    {required ? 'Falta (obligatorio)' : 'Sin dato'}
  </span>
);

const hasVal = (v) =>
  !(v === null || v === undefined || (typeof v === 'string' && v.trim() === ''));

export const EditableText = ({
  editing,
  value,
  onChange,
  placeholder = '',
  required = false,
  type = 'text',
  suffix = '',
}) => {
  if (editing) {
    return (
      <input
        type={type}
        className={`${styles.fieldInput} ${
          required && !hasVal(value) ? styles.inputMissing : ''
        }`}
        value={value ?? ''}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  if (!hasVal(value)) return <EmptyMark required={required} />;
  return (
    <span className={styles.fieldValue}>
      {value}
      {suffix}
    </span>
  );
};

export const EditableNumber = (props) => (
  <EditableText {...props} type="number" />
);

export const EditableTextarea = ({
  editing,
  value,
  onChange,
  placeholder = '',
  required = false,
  rows = 5,
}) => {
  if (editing) {
    return (
      <textarea
        rows={rows}
        className={`${styles.fieldTextarea} ${
          required && !hasVal(value) ? styles.inputMissing : ''
        }`}
        value={value ?? ''}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  if (!hasVal(value)) return <EmptyMark required={required} />;
  return <p className={styles.descriptionText}>{value}</p>;
};

export const EditableSelect = ({
  editing,
  value,
  onChange,
  options = [],
  required = false,
  displayValue,
}) => {
  if (editing) {
    return (
      <select
        className={`${styles.fieldInput} ${
          required && !hasVal(value) ? styles.inputMissing : ''
        }`}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">Seleccionar…</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    );
  }
  if (!hasVal(value)) return <EmptyMark required={required} />;
  return <span className={styles.fieldValue}>{displayValue ?? value}</span>;
};

// Toggle booleano individual (ej: isFinished).
export const EditableToggle = ({ editing, value, onChange, label }) => {
  const active = value === true || value === 'true';
  if (editing) {
    return (
      <label className={styles.toggleRow}>
        <input
          type="checkbox"
          checked={active}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span>{label}</span>
      </label>
    );
  }
  return (
    <span className={active ? styles.chip : styles.chipOff}>
      {active ? '✓' : '✕'} {label}
    </span>
  );
};

// Grupo de checkboxes (amenities, características, ambientes, servicios, detalles).
// values: objeto { clave: boolean }. labelsMap: { clave: 'Etiqueta' }.
export const EditableCheckboxGroup = ({
  editing,
  values = {},
  labelsMap = {},
  onChange,
}) => {
  const keys = Object.keys(labelsMap);
  const isOn = (k) => values[k] === true || values[k] === 'true';

  if (editing) {
    return (
      <div className={styles.checkboxGrid}>
        {keys.map((k) => (
          <label key={k} className={styles.checkboxItem}>
            <input
              type="checkbox"
              checked={isOn(k)}
              onChange={(e) => onChange(k, e.target.checked)}
            />
            <span>{labelsMap[k]}</span>
          </label>
        ))}
      </div>
    );
  }

  const activeKeys = keys.filter(isOn);
  if (activeKeys.length === 0) {
    return <span className={styles.emptyTag}>No especificado</span>;
  }
  return (
    <div className={styles.chipList}>
      {activeKeys.map((k) => (
        <span key={k} className={styles.chip}>
          {labelsMap[k]}
        </span>
      ))}
    </div>
  );
};
